import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { StyleSheet, View, ViewStyle, Pressable, Platform } from "react-native";
import Text from "../components/Text";
import { useTheme } from "../hooks/use-theme";
import { hexToRgba } from "../utils/utils";
import { GestureDetector, Gesture } from "react-native-gesture-handler";
import { ShadowedView, shadowStyle } from "react-native-fast-shadow";
import * as ExpoHaptics from "expo-haptics";
import Animated, {
    Easing,
    ReduceMotion,
    useAnimatedStyle,
    useSharedValue,
    withSpring,
    withTiming,
} from "react-native-reanimated";
import { scheduleOnRN } from "react-native-worklets";

type SwitchProps = {
    value: boolean;
    onChange?: (state: boolean) => void;
    disabled?: boolean;
    label?: string;
    labelPosition?: "left" | "right";
    testID?: string;
    style?: ViewStyle;
    thumbColor?: string;
    ios_backgroundColor?: string;
    trackColor?: { true?: string; false?: string };
    size?: number;
};

const AnimatedShadowView = Animated.createAnimatedComponent(ShadowedView);

const ANIMATION_CONFIG = {
    duration: 850,
    dampingRatio: 0.75,
    // stiffness: 400,
    overshootClamping: false,
    restDisplacementThreshold: 0.01,
    restSpeedThreshold: 0.01,
    reduceMotion: ReduceMotion.System,
};

const SHORT_ANIMATION_CONFIG = {
    duration: 300,
    easing: Easing.out(Easing.quad),
    reduceMotion: ReduceMotion.System,
};

const Switch = ({
    value,
    onChange,
    disabled,
    label,
    labelPosition = "right",
    testID,
    style,
    thumbColor,
    trackColor,
    size = 50,
}: SwitchProps) => {
    const { colors } = useTheme();
    const {
        switchWidth,
        switchHeight,
        switchPadding,
        thumbSize,
        extendedThumbSize,
        thumbSizeDifference,
        defaultEndPosition,
    } = useMemo(() => {
        const switchWidth = size;
        const switchHeight = size * 0.62;
        const switchPadding = size * 0.06;
        const thumbSize = switchHeight - switchPadding * 2;
        const extendedThumbSize = thumbSize * 1.25;
        const thumbSizeDifference = extendedThumbSize - thumbSize;
        const defaultEndPosition = switchWidth - thumbSize - switchPadding * 2;
        return {
            switchWidth,
            switchHeight,
            switchPadding,
            thumbSize,
            extendedThumbSize,
            thumbSizeDifference,
            defaultEndPosition,
        };
    }, [size]);

    const styles = useStyles(switchHeight, switchWidth, switchPadding);

    const getTrackColor = useCallback(
        (currentValue: boolean): string => {
            if (disabled) return hexToRgba(colors.fieldLabel, 0.5) ?? colors.fieldLabel;
            if (currentValue) {
                return trackColor?.true || colors.secondary;
            } else {
                return trackColor?.false || colors.backdrop;
            }
        },
        [colors.backdrop, colors.fieldLabel, colors.secondary, disabled, trackColor],
    );

    const getThumbColor = useCallback(
        (currentValue: boolean): string => {
            if (disabled) return colors.greyAccessible;
            return currentValue ? colors.accentLighter : thumbColor || colors.background;
        },
        [colors.accentLighter, colors.background, colors.greyAccessible, disabled, thumbColor],
    );

    // We keep `onPressOut` referentially stable so the `GestureDetector` never
    // re-attaches mid-interaction (which would interrupt the active gesture).
    // To still observe the latest `value` prop from inside that stable callback
    // we mirror it into a ref. The write is deferred to `useEffect` (never in
    // render) so it stays compatible with concurrent rendering.
    // `react-hooks/refs` can't see this design and flags the ref read inside
    // `onPressOut` when it's passed to gesture builders; the warning is a known
    // false positive for stable event handlers and is silenced where it fires.
    const valueRef = useRef(value);
    useEffect(() => {
        valueRef.current = value;
    }, [value]);

    const switchHasMoved = useSharedValue(false);
    const switchState = useSharedValue(value);
    const gestureStartState = useSharedValue(value);

    const endPosition = useSharedValue(defaultEndPosition);

    const trackAnimatedColor = useSharedValue<string>(getTrackColor(value));
    const thumbAnimatedColor = useSharedValue<string>(getThumbColor(value));

    useEffect(() => {
        trackAnimatedColor.value = withTiming(getTrackColor(value), SHORT_ANIMATION_CONFIG);
        thumbAnimatedColor.value = withTiming(getThumbColor(value), SHORT_ANIMATION_CONFIG);
    }, [
        disabled,
        getThumbColor,
        getTrackColor,
        thumbAnimatedColor,
        trackAnimatedColor,
        trackColor,
        value,
    ]);

    const trackAnimatedStyles = useAnimatedStyle(() => ({
        backgroundColor: trackAnimatedColor.value,
    }));

    const thumbColorAnimatedStyles = useAnimatedStyle(() => ({
        backgroundColor: thumbAnimatedColor.value,
    }));

    const thumbLeftPosition = useSharedValue(value ? defaultEndPosition : 0);
    const thumbWidth = useSharedValue(thumbSize);

    const thumbAnimatedStyles = useAnimatedStyle(() => ({
        transform: [{ translateX: thumbLeftPosition.value }],
        width: thumbWidth.value,
    }));

    const vibrate = useCallback(() => {
        if (Platform.OS !== "web") {
            void ExpoHaptics.impactAsync(ExpoHaptics.ImpactFeedbackStyle.Light);
        }
    }, []);

    const onChangeValue = useCallback(
        (newValue: boolean, isExtended?: boolean) => {
            switchState.value = newValue;
            switchHasMoved.value = true;

            trackAnimatedColor.value = withTiming(getTrackColor(newValue), SHORT_ANIMATION_CONFIG);
            thumbAnimatedColor.value = withTiming(getThumbColor(newValue), SHORT_ANIMATION_CONFIG);

            if (newValue) {
                thumbLeftPosition.value = withSpring(
                    defaultEndPosition - (isExtended ? thumbSizeDifference : 0),
                    ANIMATION_CONFIG,
                );
            } else {
                thumbLeftPosition.value = withSpring(0, ANIMATION_CONFIG);
            }
        },
        [
            defaultEndPosition,
            getThumbColor,
            getTrackColor,
            switchHasMoved,
            switchState,
            thumbAnimatedColor,
            thumbLeftPosition,
            thumbSizeDifference,
            trackAnimatedColor,
        ],
    );

    const onPressIn = useCallback(() => {
        if (disabled) {
            return;
        }
        switchHasMoved.value = false;

        thumbWidth.value = withTiming(extendedThumbSize, SHORT_ANIMATION_CONFIG);
        endPosition.value = withTiming(
            defaultEndPosition - thumbSizeDifference,
            SHORT_ANIMATION_CONFIG,
        );
        if (switchState.value) {
            thumbLeftPosition.value = withTiming(
                thumbLeftPosition.value - thumbSizeDifference,
                SHORT_ANIMATION_CONFIG,
            );
        }
    }, [
        defaultEndPosition,
        disabled,
        endPosition,
        extendedThumbSize,
        switchHasMoved,
        switchState,
        thumbLeftPosition,
        thumbSizeDifference,
        thumbWidth,
    ]);

    const onPressOut = useCallback(() => {
        if (disabled) {
            return;
        }
        const currentValue = valueRef.current;
        const hasMoved = switchHasMoved.value;
        if (!switchHasMoved.value && switchState.value !== currentValue) {
            // External value changed between pressIn and pressOut.
            // Reset the thumb to the correct external state to avoid visual desync.
            thumbWidth.value = withTiming(thumbSize, SHORT_ANIMATION_CONFIG);
            onChangeValue(currentValue);
            if (!hasMoved) {
                onChange?.(currentValue);
            }
            return;
        }

        let newSwitchState;

        if (!switchHasMoved.value) {
            newSwitchState = !switchState.value;
            vibrate();
        } else {
            newSwitchState = switchState.value;
        }
        onChangeValue(newSwitchState);
        // In some rare cases, the onPressOut() event is triggered twice or more, with switchHasMoved.value === true.
        // In that case, don't call onChange again with the same value.
        if (!hasMoved) {
            onChange?.(newSwitchState);
        }

        thumbWidth.value = withTiming(thumbSize, SHORT_ANIMATION_CONFIG);

        switchState.value = newSwitchState;
    }, [
        disabled,
        onChange,
        onChangeValue,
        switchHasMoved,
        switchState,
        thumbSize,
        thumbWidth,
        vibrate,
    ]);

    useEffect(() => {
        if (value !== switchState.value) {
            onChangeValue(value);
        }
    }, [onChangeValue, switchState, value]);

    // Extracted JS-side callback used from the Pan gesture's `.onEnd` worklet.
    // Best practice: pass a stable JS function reference + primitive args to
    // `scheduleOnRN`, never a closure created inside the worklet. Inline closures
    // capture non-host values, which is brittle (caused a SIGABRT on
    // `isHostFunction(runtime)` in react-native-worklets 0.7.x — see
    // https://github.com/software-mansion/react-native-reanimated/pull/9386).
    const onPanEnd = useCallback(
        (finalState: boolean, hasActuallyChanged: boolean) => {
            thumbWidth.value = withTiming(thumbSize, SHORT_ANIMATION_CONFIG);
            if (hasActuallyChanged) {
                onChange?.(finalState);
            }
        },
        [onChange, thumbSize, thumbWidth],
    );

    const thumbPan = useMemo(
        () =>
            Gesture.Simultaneous(
                Gesture.Tap()
                    .runOnJS(true)
                    .shouldCancelWhenOutside(false)
                    .maxDuration(10000)
                    // `onPressOut` is a stable useCallback that reads `valueRef.current`
                    // (mirrored from `value` via an effect — see comment on `valueRef`).
                    // Keeping it stable is required to avoid re-attaching the gesture
                    // mid-interaction. The lint rule can't distinguish render-time ref
                    // reads from event-handler reads and warns conservatively here.
                    // eslint-disable-next-line react-hooks/refs
                    .onTouchesUp(() => {
                        if (Platform.OS !== "web") {
                            scheduleOnRN(onPressOut);
                        }
                    })
                    .enabled(!disabled),
                Gesture.Pan()
                    .runOnJS(true)
                    .minDistance(10)
                    .onBegin(() => {
                        gestureStartState.value = switchState.value;
                    })
                    .onUpdate((e) => {
                        if (!switchState.value) {
                            if (e.x > switchWidth / 2) {
                                scheduleOnRN(vibrate);
                                scheduleOnRN(onChangeValue, true, true);
                            }
                        } else {
                            if (e.x - extendedThumbSize < -(switchWidth / 2)) {
                                scheduleOnRN(vibrate);
                                scheduleOnRN(onChangeValue, false, true);
                            }
                        }
                    })
                    .onEnd(() => {
                        const finalState = switchState.value;
                        const hasActuallyChanged = finalState !== gestureStartState.value;
                        scheduleOnRN(onPanEnd, finalState, hasActuallyChanged);
                    })
                    .enabled(!disabled),
            ),
        [
            disabled,
            extendedThumbSize,
            gestureStartState,
            onChangeValue,
            onPanEnd,
            onPressOut,
            switchState,
            switchWidth,
            vibrate,
        ],
    );

    return (
        <View style={[styles.container, style]}>
            {labelPosition === "left" && label && (
                <Text
                    style={styles.labelLeft}
                    onPress={() => {
                        if (!disabled) {
                            onChangeValue(!value);
                            onChange?.(!value);
                            vibrate();
                        }
                    }}
                >
                    {label}
                </Text>
            )}
            <Pressable
                onPress={onPressOut}
                onPressIn={onPressIn}
                testID={testID}
                disabled={disabled}
            >
                <Animated.View style={[styles.switch, trackAnimatedStyles]}>
                    <AnimatedShadowView
                        style={[
                            {
                                height: thumbSize,
                                width: thumbSize,
                                borderRadius: thumbSize / 2,
                            },
                            shadowStyle({
                                opacity: 0.12,
                                radius: 6,
                                offset: [0, 2.5],
                            }),
                            thumbAnimatedStyles,
                            thumbColorAnimatedStyles,
                        ]}
                    >
                        <GestureDetector gesture={thumbPan}>
                            <View style={styles.thumb} />
                        </GestureDetector>
                    </AnimatedShadowView>
                </Animated.View>
            </Pressable>
            {labelPosition === "right" && label && (
                <Text
                    style={styles.labelRight}
                    onPress={() => {
                        if (!disabled) {
                            onChangeValue(!value);
                            onChange?.(!value);
                            vibrate();
                        }
                    }}
                >
                    {label}
                </Text>
            )}
        </View>
    );
};

const useStyles = (switchHeight: number, switchWidth: number, switchPadding: number) => {
    const { fonts } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    flexDirection: "row",
                    alignItems: "center",
                    borderWidth: 0, // Keep this to avoid border cutting
                },
                switch: {
                    height: switchHeight,
                    width: switchWidth,
                    borderRadius: switchHeight / 2,
                    padding: switchPadding,
                    overflow: "hidden",
                },
                thumb: {
                    flex: 1,
                },
                labelLeft: {
                    ...fonts.bodySmall,
                    flexGrow: 0,
                    flexShrink: 1,
                    marginRight: 10,
                },
                labelRight: {
                    ...fonts.bodySmall,
                    flexGrow: 1,
                    flexShrink: 1,
                    marginLeft: 10,
                },
            }),
        [fonts.bodySmall, switchHeight, switchPadding, switchWidth],
    );
};

export default Switch;
